/**
 * Language and direction.
 *
 * The legacy panel switches culture with a cookie and a full round-trip: the
 * server re-renders every view against `Locale/*.resx` (`Foo.resx` is English,
 * `Foo.ar.resx` Arabic) and the layout swaps `dir`. This module is the same
 * contract, client-side.
 *
 * Two deliberate choices:
 *
 * 1. The active locale is resolved ONCE, at module-evaluation time, and a
 *    switch reloads the page. Half this codebase builds its column sets, filter
 *    options and chart series in module-level constants — resolving per render
 *    would leave those frozen in whichever language happened to load first.
 *    A reload is what the legacy does, costs nothing on a mock-data app, and
 *    makes every string in the tree correct by construction.
 *
 * 2. Strings live in two parallel modules per feature (`strings.ar.js` and
 *    `strings.en.js`) and `pickLocale` merges them. English is layered OVER
 *    Arabic, so a key that has no translation yet renders its Arabic rather
 *    than `undefined` — an untranslated label is a small blemish, a blank one
 *    is a broken screen.
 */

import { translate } from './dictionary'

const STORAGE_KEY = 'rawam.locale'

export const LOCALES = {
  ar: {
    code: 'ar',
    dir: 'rtl',
    /** What the switcher offers this language AS, in its own script. */
    label: 'العربية',
    /* `AR`, not `ع`. The compact switcher shows one short form beside a globe,
       and a single Arabic letter is not a short form of anything — it is the
       letter ain, which names no language on its own and sits visually tiny
       next to `EN`. The two-letter ISO code is what the pair needs to be read
       as a pair. Latin in both, deliberately: this is a CODE, not prose. */
    short: 'AR',
    /** Intl tag for dates and numbers. */
    tag: 'ar-SA',
    /** The culture the SERVER supports — what goes in `.AspNetCore.Culture`. */
    culture: 'ar-SA',
    title: 'روام — منصة التوعية الأمنية',
  },
  en: {
    code: 'en',
    dir: 'ltr',
    label: 'English',
    short: 'EN',
    /* en-GB, not en-US: every date in the app is printed day-month-year, the
       way DateUtil.FormatDateTime prints it. The WORDING is American, because
       the legacy's own English resources are — "License", "Program", "Center
       of Excellence" — and written strings must not drift from them. */
    tag: 'en-GB',
    /* `en-US`, not `en-GB`: those are the only two cultures Program.cs registers as
       supported, and an unsupported value silently falls back to Arabic — which would
       show an English panel with Arabic server messages in it. */
    culture: 'en-US',
    title: 'Rawam — Security Awareness Platform',
  },
}

/**
 * The server decides the culture of everything it returns — validation text, action
 * results, exported file names — from `.AspNetCore.Culture`, in the order query string
 * → cookie → `Accept-Language` → Arabic (`Program.cs`). This app switches language
 * client-side, so unless it writes the same cookie the two drift apart and a screen
 * ends up English with Arabic messages inside it.
 *
 * The value is `CookieRequestCultureProvider`'s own format, URL-encoded exactly as the
 * server writes it: `c=ar-SA|uic=ar-SA`.
 */
const CULTURE_COOKIE = '.AspNetCore.Culture'

/**
 * Expires every OTHER `.AspNetCore.Culture` the browser may be holding.
 *
 * `document.cookie` writes one cookie — the one matching the path and domain given. A
 * cookie of the same name left on a parent domain or a deeper path is a SEPARATE cookie
 * that this app can neither see nor overwrite, and the browser sends both. ASP.NET Core
 * keeps the LAST of the duplicates, so which language the server answers in comes down to
 * cookie ordering: verified against a running instance, `en-US; ar-SA` answers Arabic
 * while `ar-SA; en-US` answers English. An English panel then shows Arabic messages while
 * every reload writes a correct cookie that never wins.
 *
 * This is an upgrade over an installed product (roadmap §4) and the legacy panel switched
 * culture with this same cookie, so a twin from before the migration is exactly what a
 * long-standing client's browser is likely to have. Clearing is best-effort — expiry has
 * to name the same path and domain the cookie was set with, so it covers the current
 * path's ancestors and the host's parent domains, which is where a same-origin app can
 * have put one.
 */
function clearCultureTwins() {
  const past = 'expires=Thu, 01 Jan 1970 00:00:00 GMT'

  const segments = window.location.pathname.split('/').filter(Boolean)
  const paths = ['/', ...segments.map((_, index) => `/${segments.slice(0, index + 1).join('/')}`)]

  const labels = window.location.hostname.split('.')
  // '' is the host-only cookie; the rest are the domains a parent could have claimed.
  const domains = ['']
  for (let index = 0; index <= labels.length - 2; index += 1) {
    const domain = labels.slice(index).join('.')
    domains.push(`;domain=${domain}`, `;domain=.${domain}`)
  }

  for (const path of paths) {
    for (const domain of domains) {
      document.cookie = `${CULTURE_COOKIE}=;path=${path};${past}${domain}`
    }
  }
}

/**
 * The server decides the culture of everything it returns — validation text, action
 * results, exported file names — from `.AspNetCore.Culture`, in the order query string
 * → cookie → `Accept-Language` → Arabic (`Program.cs`). This app switches language
 * client-side, so unless it writes the same cookie the two drift apart and a screen
 * ends up English with Arabic messages inside it.
 *
 * The value is `CookieRequestCultureProvider`'s own format, URL-encoded exactly as the
 * server writes it: `c=ar-SA|uic=ar-SA`.
 */
function writeCultureCookie(code) {
  const { culture } = LOCALES[code]
  const value = encodeURIComponent(`c=${culture}|uic=${culture}`)
  const year = 365 * 24 * 60 * 60

  // Before the write, so the cookie left standing is the only one of its name.
  clearCultureTwins()
  document.cookie = `${CULTURE_COOKIE}=${value};path=/;max-age=${year};samesite=lax`
}

function read() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved && LOCALES[saved]) return saved
  } catch {
    /* storage unavailable (private mode) — Arabic is the default anyway */
  }
  return 'ar'
}

/** Resolved once. See the note above on why this is not reactive. */
const active = read()

export const locale = LOCALES[active]
export const localeCode = active
export const dir = locale.dir
export const isRtl = dir === 'rtl'
export const isEnglish = active === 'en'

/**
 * THE SERVER'S CATCH-ALL LINES, which say nothing a screen does not already know.
 *
 * Handlers answer with these whatever was asked of them: "تم الحفظ بنجاح" is returned 39
 * times across the API, by save, add, update, activate, assign and copy alike, and
 * "حدث خطأ أثناء الحفظ" 32 times. When one of them is shown in place of the calling
 * screen's own wording the reader is told less than the button they pressed already told
 * them — and on a copy it was actively wrong, since the server sees a create and says
 * "saved".
 *
 * So these are dropped and the screen's own sentence stands. Anything NOT on this list is
 * kept: "نص السؤال باللغة العربية موجود مسبقا", "اسم الموضوع مستخدم من قبل", a row number,
 * a name, a count — those carry a reason the client cannot know and are the whole value of
 * a server message.
 *
 * Matched after casefolding and stripping trailing punctuation, so "Saved Successfully"
 * and "Saved successfully." are the same line.
 */
const SERVER_BOILERPLATE = new Set([
  // Success
  'تم الحفظ بنجاح',
  'تم التحديث بنجاح',
  'تمت الإضافة بنجاح',
  'تم الحذف بنجاح',
  'تم حذف المدخل بنجاح',
  'تم تحديث البيانات بنجاح',
  'تمت العملية بنجاح',
  'saved successfully',
  'updated successfully',
  'added successfully',
  'deleted successfully',
  'operation completed successfully',
  // Failure
  'حدث خطأ أثناء الحفظ',
  'حدث خطأ أثناء الحذف',
  'حدث خطأ أثناء التحديث',
  'فشلت العملية',
  'حدث خطأ',
  'error occurred while saving',
  'error occurred while deleting',
  'error occurred while updating',
  'operation failed',
  'an error occurred',
  // The global error middleware's two lines — a status, not a reason.
  'bad request',
  'internal application server error',
])

/** Trailing full stops and exclamation marks are noise when matching a known line. */
const boilerplate = (message) =>
  SERVER_BOILERPLATE.has(String(message).trim().replace(/[.!\s]+$/u, '').toLowerCase())

/**
 * A message the SERVER wrote, offered only to a reader who can read it.
 *
 * Nothing tells the API which language the panel is in — no culture parameter, no
 * Accept-Language — so it answers in its default, which is Arabic, and it does so on
 * success as well as on failure. `toast.success(outcome.message || S.saved)` therefore put
 * an Arabic sentence on an English screen every single time an action worked.
 *
 * In English it returns nothing, so the `||` beside it falls through to the panel's own
 * string — which is the same sentence, in the right language. In Arabic it keeps the
 * server's wording, EXCEPT where that wording is one of the catch-all lines above: those
 * say less than the screen's own sentence, so they give way to it.
 *
 * SUCCESS ONLY, deliberately. An error keeps the server's text even in English — see
 * `serverDetail`, which is the error-side companion to this.
 */
export function serverText(message) {
  if (!message || boilerplate(message)) return ''
  return active === 'en' ? '' : message
}

/**
 * The same judgement for a FAILURE: the server's reason when it gave one, nothing when it
 * only said "the operation failed".
 *
 * Kept in BOTH languages, unlike `serverText`. A failure has to say why, and a specific
 * reason in the wrong language beats a generic one in the right language — "نص السؤال
 * باللغة العربية موجود مسبقا" tells an English reader far more than "the operation could
 * not be completed". The real fix is an API that answers in the caller's language
 * (API_FINDINGS.md); until then this is the better of two poor options.
 *
 * Boilerplate is dropped so the `||` beside it reaches the screen's own message, which
 * names the thing that actually failed.
 */
export function serverDetail(message) {
  if (!message || boilerplate(message)) return ''
  return message
}

/**
 * Deep-merges the English layer over the Arabic base.
 *
 * Arrays are replaced wholesale rather than merged item-by-item: a translated
 * list of tabs is a list, not a patch, and index-wise merging would silently
 * keep Arabic entries when the English one is shorter.
 */
function layer(base, over) {
  if (over === undefined || over === null) return base
  if (Array.isArray(base) || Array.isArray(over)) return over
  if (typeof base !== 'object' || typeof over !== 'object') return over
  const out = { ...base }
  for (const key of Object.keys(over)) out[key] = layer(base[key], over[key])
  return out
}

/** The one call every `strings.js` makes. Arabic short-circuits to zero work. */
export function pickLocale(ar, en) {
  return active === 'en' ? layer(ar, en) : ar
}

/**
 * The mock data, in the active language.
 *
 * Every `data/*.js` module wraps its exports in this. In Arabic it is the
 * identity function and costs nothing; in English it walks the structure once,
 * at module load, and swaps any string the dictionary has a label for. Strings
 * it does not know — a person's name, a phishing lure, an exam question — come
 * through untouched, which is the intended behaviour and not a gap. See
 * ./dictionary.js for why.
 *
 * Once the API lands this is the same call, made on the response instead.
 */
export function localize(value) {
  if (active !== 'en' || value == null) return value
  if (typeof value === 'string') return translate(value) ?? value
  if (Array.isArray(value)) return value.map(localize)
  if (typeof value !== 'object') return value
  const out = {}
  for (const key of Object.keys(value)) {
    out[key] = ARABIC_FIELD.test(key) ? value[key] : localize(value[key])
  }
  return out
}

/**
 * Fields whose whole job is to hold the ARABIC version of something.
 *
 * The organizations table has an "Arabic name" column beside an "English name"
 * one; translating the first left the screen showing the same string twice and
 * quietly destroyed the only thing that page is for. A field that names its own
 * language is never translated — in either direction.
 */
const ARABIC_FIELD = /^arabic|(Ar|AR|_AR_)(Name)?$/

export function setLocale(code) {
  if (!LOCALES[code] || code === active) return
  try {
    localStorage.setItem(STORAGE_KEY, code)
  } catch {
    /* see read() */
  }
  /* Before the reload, so the very first request of the new page already carries the
     new culture and the server never answers one language behind. */
  writeCultureCookie(code)
  /* A reload, not a re-render — see the note at the top of this file. */
  window.location.reload()
}

/** The other language, for a two-state switcher. */
export const otherLocale = LOCALES[active === 'ar' ? 'en' : 'ar']

/**
 * Called once before first paint, alongside `bootBranding`, so the document
 * never flashes the wrong direction. index.html ships `lang="ar" dir="rtl"`
 * because Arabic is the default; this corrects it when English is stored.
 */
export function bootLocale() {
  const root = document.documentElement
  root.lang = locale.code
  root.dir = locale.dir
  root.dataset.locale = locale.code
  document.title = locale.title

  /* Also on boot, not only on switch: the stored preference survives a cleared cookie,
     a new browser profile and a fresh install, and without this the panel would come up
     in the stored language while the server kept answering in the default one. */
  writeCultureCookie(active)
}
